// Step-up two-factor confirmation for high-risk actions (payroll generate /
// finalize / unfinalize). Prompts for the 6-digit authenticator code using a
// segmented box input, runs the supplied action with it, and re-prompts if the
// API rejects the code. Other errors bubble up to the caller's own handling.
//
//   const result = await withTwoFactorCode(this.$swal, (code) => finalizeRun(id, code));
//   if (result?.cancelled) return;   // user dismissed the prompt
//
// Returns the action's resolved value, or { cancelled: true } if dismissed.
const OTP_LEN = 6;

// One-time injection of the dialog + segmented-input styles.
function ensureOtpStyles() {
  if (typeof document === "undefined" || document.getElementById("ws-otp-style")) return;
  const style = document.createElement("style");
  style.id = "ws-otp-style";
  style.textContent = `
    .ws-2fa-pop { border-radius: 22px !important; padding: 30px 28px 26px !important; box-shadow: 0 24px 64px rgba(16,24,40,.24) !important; }
    .ws-2fa-actions { gap: 10px !important; margin-top: 22px !important; }
    .ws-2fa-confirm { border-radius: 12px !important; font-weight: 600 !important; padding: 11px 28px !important; box-shadow: 0 10px 22px rgba(245,124,0,.30) !important; transition: transform .12s ease, box-shadow .18s ease; }
    .ws-2fa-confirm:hover { transform: translateY(-1px); }
    .ws-2fa-confirm:focus { box-shadow: 0 0 0 3px rgba(245,124,0,.32) !important; }
    .ws-2fa-cancel { border-radius: 12px !important; font-weight: 600 !important; padding: 11px 24px !important; background: #eef1f5 !important; color: #475569 !important; }
    .ws-2fa-cancel:hover { background: #e3e7ee !important; }

    .ws-otp-head { text-align: center; }
    .ws-otp-icon {
      display: inline-flex; align-items: center; justify-content: center;
      width: 58px; height: 58px; border-radius: 50%;
      background: linear-gradient(135deg, #fff1e0, #ffd6a8);
      color: #F57C00; font-size: 1.85rem;
      box-shadow: 0 10px 22px rgba(245,124,0,.24);
      margin-bottom: 14px;
    }
    .ws-otp-title { font-size: 1.35rem; font-weight: 700; color: #111827; margin: 0 0 6px; }
    .ws-otp-desc { color: #6b7280; font-size: .92rem; line-height: 1.5; margin: 0 auto; max-width: 330px; }

    .ws-otp { display: flex; gap: 12px; justify-content: center; margin: 22px 0 4px; }
    .ws-otp__box {
      width: 52px; height: 62px; padding: 0; text-align: center;
      font-size: 1.6rem; font-weight: 600; color: #1f2937;
      font-variant-numeric: tabular-nums;
      border: 1.5px solid #e5e7eb; border-radius: 14px; background: #fff;
      box-shadow: 0 1px 2px rgba(16,24,40,.06);
      caret-color: #F57C00; outline: none;
      transition: border-color .18s ease, box-shadow .18s ease, transform .18s ease, background .18s ease;
      -moz-appearance: textfield;
    }
    .ws-otp__box::-webkit-outer-spin-button,
    .ws-otp__box::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
    .ws-otp__box:hover { border-color: #d6dae0; }
    .ws-otp__box:focus {
      border-color: #F57C00;
      box-shadow: 0 0 0 4px rgba(245,124,0,.15), 0 8px 18px rgba(245,124,0,.18);
      transform: translateY(-2px);
    }
    .ws-otp__box.is-filled { border-color: #F57C00; background: #fff8f1; animation: ws-otp-pop .18s ease; }
    @keyframes ws-otp-pop { 0% { transform: scale(.9); } 60% { transform: scale(1.05); } 100% { transform: scale(1); } }
    @media (max-width: 420px) {
      .ws-otp { gap: 8px; }
      .ws-otp__box { width: 44px; height: 54px; font-size: 1.4rem; }
    }
  `;
  document.head.appendChild(style);
}

function buildOtpHtml() {
  let boxes = "";
  for (let i = 0; i < OTP_LEN; i += 1) {
    boxes += `<input class="ws-otp__box" type="text" inputmode="numeric" autocomplete="${
      i === 0 ? "one-time-code" : "off"
    }" maxlength="1" aria-label="Digit ${i + 1}" />`;
  }
  return `
    <div class="ws-otp-head">
      <span class="ws-otp-icon"><i class="bx bx-shield-quarter"></i></span>
      <h2 class="ws-otp-title">Two-factor confirmation</h2>
      <p class="ws-otp-desc">Enter the 6-digit code from your authenticator app to confirm this action.</p>
    </div>
    <div class="ws-otp">${boxes}</div>
  `;
}

function readOtp(popup) {
  if (!popup) return "";
  return Array.from(popup.querySelectorAll(".ws-otp__box"))
    .map((b) => b.value)
    .join("");
}

// Wire up auto-advance, backspace, arrows, paste and Enter-to-submit.
function wireOtp($swal, popup) {
  const boxes = Array.from(popup.querySelectorAll(".ws-otp__box"));
  const setVal = (el, v) => {
    el.value = v;
    el.classList.toggle("is-filled", !!v);
  };

  boxes.forEach((box, idx) => {
    box.addEventListener("focus", () => box.select());
    box.addEventListener("input", () => {
      const d = box.value.replace(/\D/g, "").slice(-1);
      setVal(box, d);
      if (d && idx < boxes.length - 1) boxes[idx + 1].focus();
    });
    box.addEventListener("keydown", (e) => {
      if (e.key === "Enter") {
        $swal.clickConfirm();
      } else if (e.key === "Backspace") {
        if (box.value) {
          setVal(box, "");
        } else if (idx > 0) {
          boxes[idx - 1].focus();
          setVal(boxes[idx - 1], "");
        }
        e.preventDefault();
      } else if (e.key === "ArrowLeft" && idx > 0) {
        boxes[idx - 1].focus();
        e.preventDefault();
      } else if (e.key === "ArrowRight" && idx < boxes.length - 1) {
        boxes[idx + 1].focus();
        e.preventDefault();
      }
    });
    box.addEventListener("paste", (e) => {
      e.preventDefault();
      const text = (e.clipboardData || window.clipboardData).getData("text").replace(/\D/g, "").slice(0, boxes.length);
      if (!text) return;
      boxes.forEach((b, i) => setVal(b, text[i] || ""));
      boxes[Math.min(text.length, boxes.length - 1)].focus();
    });
  });

  if (boxes[0]) boxes[0].focus();
}

export async function withTwoFactorCode($swal, action) {
  ensureOtpStyles();
  // eslint-disable-next-line no-constant-condition
  for (;;) {
    const { isConfirmed, value } = await $swal.fire({
      html: buildOtpHtml(),
      showCancelButton: true,
      confirmButtonText: "Confirm",
      confirmButtonColor: "#F57C00",
      focusConfirm: false,
      customClass: {
        popup: "ws-2fa-pop",
        actions: "ws-2fa-actions",
        confirmButton: "ws-2fa-confirm",
        cancelButton: "ws-2fa-cancel",
      },
      didOpen: (popup) => wireOtp($swal, popup),
      preConfirm: () => {
        const code = readOtp($swal.getPopup());
        if (!/^\d{6}$/.test(code)) {
          $swal.showValidationMessage("Enter the 6-digit code.");
          return false;
        }
        return code;
      },
    });

    if (!isConfirmed) return { cancelled: true };

    try {
      return await action(value);
    } catch (e) {
      const errCode = e.response?.data?.code;
      if (errCode === "two_factor_code_invalid" || errCode === "two_factor_code_required") {
        await $swal.fire({
          icon: "error",
          title: "Invalid code",
          text: e.response?.data?.message || "That code didn't work. Try again.",
          confirmButtonColor: "#F57C00",
        });
        continue; // re-prompt
      }
      throw e; // anything else is the caller's to handle
    }
  }
}
